import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import readXlsxFile from "read-excel-file";
import { autoCorrectRows } from "../../../lib/mfBulkInput/autoCorrectRows";
import { generateTrainRows } from "../../../lib/mfBulkInput/generateTrainRows";
import { parseExcelMatrix, parseLegacySessionStorage } from "../../../lib/mfBulkInput/parseExcelRows";
import {
  countValidation,
  validateRows,
} from "../../../lib/mfBulkInput/validateRows";
import { applyServerDuplicateCheck } from "../../../lib/mfBulkInput/validateRowsAsync";
import { TRAIN_CONDITIONS_STORAGE_KEY } from "../../../lib/mfBulkInput/constants";
import { reindexRows, updateRowField } from "../../../lib/mfBulkInput/rowUtils";

function loadInitialRows() {
  if (typeof sessionStorage === "undefined") return [];
  return parseLegacySessionStorage(sessionStorage.mfexcellist);
}

function deriveStep(mode, { rows, validatedAt, correctedAt, savedAt }) {
  if (mode === "training") {
    if (!rows.length) return 0;
    if (!validatedAt) return rows.length ? 1 : 0;
    return 2;
  }
  if (!rows.length) return 0;
  if (!validatedAt) return 1;
  if (!correctedAt && countValidation(rows).errors > 0) return 2;
  if (!savedAt) return 3;
  return 3;
}

export default function useBulkInputState(apiAddr) {
  const [mode, setMode] = useState("production");
  const [rows, setRows] = useState(loadInitialRows);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [validatedAt, setValidatedAt] = useState(null);
  const [correctedAt, setCorrectedAt] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ done: 0, total: 0 });

  const [trainCount, setTrainCount] = useState(50);
  const [trainRdvs, setTrainRdvs] = useState("");
  const [includeNonFire, setIncludeNonFire] = useState(true);

  const validationCounts = useMemo(() => countValidation(rows), [rows]);
  const activeStep = useMemo(
    () =>
      deriveStep(mode, { rows, validatedAt, correctedAt, savedAt }),
    [mode, rows, validatedAt, correctedAt, savedAt],
  );

  const visibleRows = useMemo(() => {
    if (!showErrorsOnly) return rows;
    return rows.filter((r) => r.rowStatus === "error");
  }, [rows, showErrorsOnly]);

  const syncSessionStorage = useCallback((nextRows) => {
    if (typeof sessionStorage === "undefined") return;
    const { rowToExcelArray } = require("../../../lib/mfBulkInput/rowUtils");
    const { DEFAULT_EXCEL_HEADER } = require("../../../lib/mfBulkInput/constants");
    sessionStorage.mfexcellist = JSON.stringify([
      DEFAULT_EXCEL_HEADER,
      ...nextRows.map((r) => rowToExcelArray(r)),
    ]);
  }, []);

  const setRowsAndSync = useCallback(
    (updater) => {
      setRows((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        syncSessionStorage(next);
        return next;
      });
      setValidatedAt(null);
      setCorrectedAt(null);
      setSavedAt(null);
    },
    [syncSessionStorage],
  );

  const updateField = useCallback(
    (rowId, fieldKey, value) => {
      setRows((prev) => {
        const next = prev.map((r) =>
          r.id === rowId ? updateRowField(r, fieldKey, value) : r,
        );
        syncSessionStorage(next);
        return next;
      });
      setValidatedAt(null);
    },
    [syncSessionStorage],
  );

  const deleteRow = useCallback(
    (rowId) => {
      setRowsAndSync((prev) => reindexRows(prev.filter((r) => r.id !== rowId)));
    },
    [setRowsAndSync],
  );

  const clearRows = useCallback(() => {
    setRowsAndSync([]);
    setUploadedFileName("");
  }, [setRowsAndSync]);

  const loadExcelFile = useCallback(
    async (file) => {
      if (!file) return;
      const data = await readXlsxFile(file);
      const parsed = parseExcelMatrix(data);
      setUploadedFileName(file.name);
      setRowsAndSync(parsed);
    },
    [setRowsAndSync],
  );

  const runValidation = useCallback(async () => {
    setIsValidating(true);
    try {
      let next = validateRows(rows);
      if (mode === "production") {
        const dsid =
          typeof sessionStorage !== "undefined"
            ? (sessionStorage.ondsid || "").trim()
            : "";
        next = await applyServerDuplicateCheck(next, apiAddr, dsid);
      }
      setRows(next);
      syncSessionStorage(next);
      setValidatedAt(Date.now());
    } finally {
      setIsValidating(false);
    }
  }, [rows, mode, apiAddr, syncSessionStorage]);

  const runAutoCorrect = useCallback(async () => {
    const { rows: corrected, changed } = autoCorrectRows(rows);
    syncSessionStorage(corrected);
    if (changed) setCorrectedAt(Date.now());

    setIsValidating(true);
    try {
      let next = validateRows(corrected);
      if (mode === "production") {
        const dsid =
          typeof sessionStorage !== "undefined"
            ? (sessionStorage.ondsid || "").trim()
            : "";
        next = await applyServerDuplicateCheck(next, apiAddr, dsid);
      }
      setRows(next);
      syncSessionStorage(next);
      setValidatedAt(Date.now());
    } finally {
      setIsValidating(false);
    }
  }, [rows, mode, apiAddr, syncSessionStorage]);

  const generateTraining = useCallback(() => {
    const generated = generateTrainRows({
      count: trainCount,
      rdvName: trainRdvs,
      distribution: "equal",
      phoneMode: "random",
      includeNonFire,
    });
    setRowsAndSync(generated);
    setUploadedFileName("");
  }, [trainCount, trainRdvs, includeNonFire, setRowsAndSync]);

  const saveTrainConditions = useCallback(() => {
    try {
      localStorage.setItem(
        TRAIN_CONDITIONS_STORAGE_KEY,
        JSON.stringify({
          trainCount,
          trainRdvs,
          includeNonFire,
        }),
      );
    } catch {
      /* ignore */
    }
  }, [trainCount, trainRdvs, includeNonFire]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(TRAIN_CONDITIONS_STORAGE_KEY);
      if (!raw) return;
      const c = JSON.parse(raw);
      if (c.trainCount) setTrainCount(c.trainCount);
      if (c.trainRdvs) setTrainRdvs(c.trainRdvs);
      if (typeof c.includeNonFire === "boolean") setIncludeNonFire(c.includeNonFire);
    } catch {
      /* ignore */
    }
  }, []);

  const fillRandomPhones = useCallback(() => {
    let filled = 0;
    setRows((prev) => {
      const next = prev.map((row, i) => {
        if (String(row.pnum || "").trim()) return row;
        const r1 = String(Math.floor(Math.random() * 9000) + 1000);
        const r2 = String(Math.floor(Math.random() * 9000) + 1000);
        filled += 1;
        return updateRowField(
          {
            ...row,
            name: String(row.name || "").trim() || `훈련자${i + 1}`,
          },
          "pnum",
          `000-${r1}-${r2}`,
        );
      });
      syncSessionStorage(next);
      return next;
    });
    return filled;
  }, [syncSessionStorage]);

  const clearPhoneFields = useCallback(() => {
    setRows((prev) => {
      const next = prev.map((row) =>
        updateRowField(updateRowField(row, "pnum", ""), "name", ""),
      );
      syncSessionStorage(next);
      return next;
    });
  }, [syncSessionStorage]);

  const applyQrEntries = useCallback(
    (entries) => {
      setRows((prev) => {
        const next = prev.map((r) => ({ ...r }));
        let slot = 0;
        entries.forEach((entry) => {
          while (slot < next.length && String(next[slot].pnum || "").trim()) {
            slot += 1;
          }
          if (slot >= next.length) return;
          next[slot] = updateRowField(
            updateRowField(next[slot], "pnum", entry.pnum || ""),
            "name",
            entry.name || "",
          );
          slot += 1;
        });
        syncSessionStorage(next);
        return next;
      });
    },
    [syncSessionStorage],
  );

  return {
    mode,
    setMode,
    rows,
    setRows,
    visibleRows,
    uploadedFileName,
    validatedAt,
    correctedAt,
    savedAt,
    setSavedAt,
    showErrorsOnly,
    setShowErrorsOnly,
    isValidating,
    isSaving,
    setIsSaving,
    saveProgress,
    setSaveProgress,
    validationCounts,
    activeStep,
    trainCount,
    setTrainCount,
    trainRdvs,
    setTrainRdvs,
    includeNonFire,
    setIncludeNonFire,
    setRowsAndSync,
    updateField,
    deleteRow,
    clearRows,
    loadExcelFile,
    runValidation,
    runAutoCorrect,
    generateTraining,
    saveTrainConditions,
    fillRandomPhones,
    clearPhoneFields,
    applyQrEntries,
    syncSessionStorage,
  };
}
